
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { PaymentMethod } from '@shared/types';
import { toast } from 'sonner';
import { useUser } from './UserContext';

interface PaymentMethodContextType {
  methods: PaymentMethod[];
  getMethodById: (id: string) => PaymentMethod | undefined;
  isLoading: boolean;
  refreshMethods: () => Promise<void>;
}

const PaymentMethodContext = createContext<PaymentMethodContextType | undefined>(undefined);

export const usePaymentMethods = () => {
  const context = useContext(PaymentMethodContext);
  if (!context) {
    throw new Error('usePaymentMethods must be used within a PaymentMethodProvider');
  }
  return context;
};

export const PaymentMethodProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, fetchWithAuth } = useUser();
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadMethods = useCallback(async () => {
    if (!currentUser) {
      setMethods([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetchWithAuth('/metodos-pago');
      if (!response.ok) {
        throw new Error('Failed to fetch payment methods');
      }
      const data = await response.json();
      setMethods(data);
    } catch (error) {
      console.error('Error loading payment methods:', error);
      toast.error('Error al cargar métodos de pago', {
        description: error instanceof Error ? error.message : 'Error desconocido'
      });
      setMethods([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, fetchWithAuth]);

  useEffect(() => {
    if (currentUser) {
      loadMethods();
    } else {
      setMethods([]);
      setIsLoading(false);
    }
  }, [currentUser, loadMethods]);

  // Memoize getMethodById to prevent unnecessary re-renders
  const getMethodById = useCallback((id: string) => methods.find(m => m.id === id), [methods]);

  const value = useMemo(
    () => ({ methods, getMethodById, isLoading, refreshMethods: loadMethods }),
    [methods, getMethodById, isLoading, loadMethods]
  );

  return (
    <PaymentMethodContext.Provider value={value}>
      {children}
    </PaymentMethodContext.Provider>
  );
};
